import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface ChatbotType {
    id: string;
    name: string;
    description: string;
    status: 'draft' | 'training' | 'ready' | 'live';
    createdAt: Date;
    lastUpdated: Date;
    interactions?: number;
    successRate?: number;
    tags?: string[];
}

const AllChatbotsPage: React.FC = () => {
    // Mock-Daten für alle Chatbots
    const [chatbots, setChatbots] = useState<ChatbotType[]>([
        {
            id: '1',
            name: 'Kundenservice Bot',
            description: 'Hilft Kunden bei häufigen Fragen und Problemen',
            status: 'live',
            createdAt: new Date('2025-04-15'),
            lastUpdated: new Date('2025-05-01'),
            interactions: 2453,
            successRate: 89,
            tags: ['Service', 'FAQ']
        },
        {
            id: '2',
            name: 'Produkt FAQ Bot',
            description: 'Beantwortet Fragen zu unseren Produkten',
            status: 'draft',
            createdAt: new Date('2025-05-05'),
            lastUpdated: new Date('2025-05-05'),
            tags: ['Produkte', 'FAQ']
        },
        {
            id: '3',
            name: 'Sales Assistent',
            description: 'Unterstützt den Vertrieb bei der Lead-Generierung',
            status: 'training',
            createdAt: new Date('2025-05-02'),
            lastUpdated: new Date('2025-05-06'),
            tags: ['Sales', 'Marketing']
        },
        {
            id: '4',
            name: 'Support Bot',
            description: 'Technischer Support für unsere Software-Produkte',
            status: 'ready',
            createdAt: new Date('2025-04-28'),
            lastUpdated: new Date('2025-05-07'),
            interactions: 876,
            successRate: 72,
            tags: ['Support', 'Technik']
        },
        {
            id: '5',
            name: 'Onboarding Assistent',
            description: 'Führt neue Nutzer durch unsere Plattform',
            status: 'live',
            createdAt: new Date('2025-04-10'),
            lastUpdated: new Date('2025-05-02'),
            interactions: 1245,
            successRate: 94,
            tags: ['Onboarding', 'Tutorial']
        }
    ]);

    // Filter-Status
    const [statusFilter, setStatusFilter] = useState<string>('all');
    // Such-Term
    const [searchTerm, setSearchTerm] = useState<string>('');

    // Filtere die Chatbots basierend auf dem Status und dem Suchbegriff
    const filteredChatbots = chatbots.filter(chatbot => {
        // Status-Filter
        if (statusFilter !== 'all' && chatbot.status !== statusFilter) {
            return false;
        }

        // Such-Filter
        if (searchTerm && !chatbot.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
            !chatbot.description.toLowerCase().includes(searchTerm.toLowerCase())) {
            return false;
        }

        return true;
    });

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-900">Alle Chatbots</h1>
                <Link
                    to="/create"
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md flex items-center"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Neuen Chatbot erstellen
                </Link>
            </div>

            {/* Filter und Suche */}
            <div className="bg-white p-4 shadow rounded-lg mb-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => setStatusFilter('all')}
                            className={`px-3 py-1 text-sm font-medium rounded-md ${
                                statusFilter === 'all'
                                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            Alle
                        </button>
                        <button
                            onClick={() => setStatusFilter('live')}
                            className={`px-3 py-1 text-sm font-medium rounded-md ${
                                statusFilter === 'live'
                                    ? 'bg-green-100 text-green-700 border border-green-300'
                                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            Live
                        </button>
                        <button
                            onClick={() => setStatusFilter('ready')}
                            className={`px-3 py-1 text-sm font-medium rounded-md ${
                                statusFilter === 'ready'
                                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            Bereit
                        </button>
                        <button
                            onClick={() => setStatusFilter('training')}
                            className={`px-3 py-1 text-sm font-medium rounded-md ${
                                statusFilter === 'training'
                                    ? 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            Training
                        </button>
                        <button
                            onClick={() => setStatusFilter('draft')}
                            className={`px-3 py-1 text-sm font-medium rounded-md ${
                                statusFilter === 'draft'
                                    ? 'bg-gray-100 text-gray-700 border border-gray-300'
                                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            Entwurf
                        </button>
                    </div>

                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Suche nach Chatbots..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full md:w-64"
                        />
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* Chatbot Liste */}
            <div className="space-y-4">
                {filteredChatbots.length > 0 ? (
                    filteredChatbots.map((chatbot) => (
                        <div
                            key={chatbot.id}
                            className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden flex flex-col md:flex-row"
                        >
                            <div className="p-5 flex-1">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h2 className="text-xl font-semibold text-gray-800">{chatbot.name}</h2>
                                        <p className="mt-1 text-gray-600">{chatbot.description}</p>
                                    </div>
                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                        chatbot.status === 'live' ? 'bg-green-100 text-green-800' :
                                            chatbot.status === 'ready' ? 'bg-blue-100 text-blue-800' :
                                                chatbot.status === 'training' ? 'bg-yellow-100 text-yellow-800' :
                                                    'bg-gray-100 text-gray-800'
                                    }`}>
                    {chatbot.status === 'live' ? 'Live' :
                        chatbot.status === 'ready' ? 'Bereit' :
                            chatbot.status === 'training' ? 'Training' :
                                'Entwurf'}
                  </span>
                                </div>

                                {chatbot.tags && (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {chatbot.tags.map((tag, index) => (
                                            <span key={index} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
                        {tag}
                      </span>
                                        ))}
                                    </div>
                                )}

                                {chatbot.interactions && (
                                    <div className="mt-4 flex items-center text-sm text-gray-500">
                                        <span className="mr-4">{chatbot.interactions} Interaktionen</span>
                                        {chatbot.successRate && (
                                            <span className="font-medium text-green-600">{chatbot.successRate}% Erfolg</span>
                                        )}
                                    </div>
                                )}

                                <div className="mt-3 text-xs text-gray-500">
                                    Erstellt: {chatbot.createdAt.toLocaleDateString()} |
                                    Letzte Änderung: {chatbot.lastUpdated.toLocaleDateString()}
                                </div>
                            </div>

                            <div className="bg-gray-50 p-5 flex flex-row md:flex-col justify-between items-center space-y-0 md:space-y-3 space-x-3 md:space-x-0">
                                <Link
                                    to={`/chatbot/${chatbot.id}`}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-center w-full"
                                >
                                    {chatbot.status === 'draft' ? 'Fortsetzen' : 'Bearbeiten'}
                                </Link>
                                <Link
                                    to={`/preview/${chatbot.id}`}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-center w-full"
                                >
                                    Vorschau
                                </Link>
                                {chatbot.status === 'live' && (
                                    <Link
                                        to={`/analytics?bot=${chatbot.id}`}
                                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-center w-full"
                                    >
                                        Analytics
                                    </Link>
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="bg-white p-8 rounded-lg shadow-md text-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h3 className="mt-2 text-lg font-medium text-gray-900">Keine Chatbots gefunden</h3>
                        <p className="mt-1 text-gray-500">
                            {searchTerm
                                ? `Es wurden keine Chatbots gefunden, die "${searchTerm}" enthalten.`
                                : 'Es wurden keine Chatbots mit den ausgewählten Filtern gefunden.'}
                        </p>
                        <div className="mt-6">
                            <button
                                onClick={() => {
                                    setSearchTerm('');
                                    setStatusFilter('all');
                                }}
                                className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                Filter zurücksetzen
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AllChatbotsPage;